import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  afterNextRender,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  DialogComponent,
  DialogTitleBarComponent,
} from '@progress/kendo-angular-dialog';
import { LoaderComponent } from '@progress/kendo-angular-indicators';
import { ButtonComponent } from '@progress/kendo-angular-buttons';
import { InAppHelpModule } from '@ups/xplat/web/in-app-help';
import { DocuSignSdkLoader } from './docusign-sdk-loader.service';
import {
  DocuSignSignerRole,
  DocuSignSigningOutcome,
  DocuSignSigningRetryAction,
  DocuSignSigningSession,
  DocuSignSigningStage,
  loadingHintFor,
  outcomeFromSessionEnd,
  signingErrorMessage,
  signingStageMessage,
  signingTitleFor,
} from './docusign-signing.utils';

/**
 * Shared DocuSign embedded-signing dialog (WI 34399, decision D6), for HR (web-hr-packet) and employee
 * (web-employee-packet) signing. The host passes `sessionProvider`, which starts a session on its API; the dialog mounts
 * the DocuSign **focused view** with DocuSign.js (a plain iframe is refused) and reports how it ended from the SDK's
 * `sessionEnd`:
 * - `signing_complete` → `confirmCompletion` (the D1 reconcile, when given) is awaited, then `completed`;
 * - `decline` → `declined`; any other end, or the title-bar close → `cancelled`;
 * - a failed start shows the reason, and closing then emits `failed` with it.
 *
 * A provider that resolves `null` means there is nothing left to sign (already completed) and is reported as
 * `completed`. Closing never touches the envelope (decision D2). The kill switch (`useEmbedded: false`) or an SDK that
 * fails to load falls back to a full-page redirect to the same one-time URL.
 */
@Component({
  selector: 'ups-docusign-signing-dialog',
  imports: [
    DialogComponent,
    DialogTitleBarComponent,
    LoaderComponent,
    ButtonComponent,
    InAppHelpModule,
  ],
  templateUrl: './docusign-signing-dialog.component.html',
  styleUrl: './docusign-signing-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocuSignSigningDialogComponent {
  private readonly sdk = inject(DocuSignSdkLoader);

  /** Starts a signing session on the host's API. */
  readonly sessionProvider =
    input.required<() => Promise<DocuSignSigningSession | null>>();
  /** Optional: record the signing server side before `completed` is emitted (decision D1 reconcile). */
  readonly confirmCompletion = input<(() => Promise<unknown>) | null>(null);
  readonly signerRole = input<DocuSignSignerRole>('employee');
  /** Title-bar text; defaults by `signerRole`. */
  readonly title = input<string | null>(null);
  /** Optional second way to start, offered when the first start fails with a matching error (HR take-over). */
  readonly retryAction = input<DocuSignSigningRetryAction | null>(null);
  /** How long to wait for the signing view to report ready before showing an error. */
  readonly readyTimeoutMs = input(120_000);

  /** The signer finished signing. */
  readonly completed = output<void>();
  /** The signer declined to sign in DocuSign. */
  readonly declined = output<void>();
  /** The dialog was closed, or DocuSign ended without a signature (finish later, timeout). */
  readonly cancelled = output<void>();
  /** Signing could not start; carries the message shown, emitted when the dialog is closed. */
  readonly failed = output<string>();

  readonly loading = signal(true);
  readonly stage = signal<DocuSignSigningStage>('preparing');
  readonly stageMessage = computed(() => signingStageMessage(this.stage()));
  /** Whole seconds of the current wait; DocuSign's start is slow, so the hint shows the wait so far. */
  readonly elapsedSeconds = signal(0);
  readonly loadingHint = computed(() => loadingHintFor(this.elapsedSeconds()));
  readonly error = signal<string | null>(null);
  /** The retry action applies to the error being shown. */
  readonly canRetry = signal(false);
  readonly dialogTitle = computed(
    () => this.title()?.trim() || signingTitleFor(this.signerRole())
  );

  private readonly mount = viewChild<ElementRef<HTMLElement>>('mount');

  /** True once an outcome was emitted, so `sessionEnd` and a manual close cannot both fire. */
  private settled = false;
  /** The host removed the dialog; a start still in flight must not mount or navigate afterwards. */
  private destroyed = false;
  private readyTimer: ReturnType<typeof setTimeout> | undefined;

  constructor() {
    afterNextRender(() => void this.start());
    const ticker = setInterval(() => {
      if (this.loading()) {
        this.elapsedSeconds.update((s) => s + 1);
      }
    }, 1000);
    inject(DestroyRef).onDestroy(() => {
      this.destroyed = true;
      clearInterval(ticker);
      clearTimeout(this.readyTimer);
    });
  }

  /** Start the session and mount the view, or redirect. Public so specs can drive it without rendering. */
  async start(
    provider: () => Promise<DocuSignSigningSession | null> = this.sessionProvider()
  ): Promise<void> {
    let session: DocuSignSigningSession | null;
    try {
      session = await provider();
    } catch (e: unknown) {
      if (this.abandoned()) {
        return;
      }
      this.canRetry.set(this.retryAction()?.appliesTo(e) ?? false);
      this.fail(signingErrorMessage(e));
      return;
    }

    // Closed during the slow start (decision D2: close just closes): the envelope may now exist, but the page must not
    // follow the one-time URL into the ceremony. Continue reopens it.
    if (this.abandoned()) {
      return;
    }

    if (session === null) {
      this.settle('completed');
      return;
    }

    if (!session.url) {
      this.fail('DocuSign did not return a signing session.');
      return;
    }

    if (!session.useEmbedded || !session.integrationKey || !session.scriptUrl) {
      this.redirectToCeremony(session.url);
      return;
    }

    this.stage.set('opening');
    try {
      const docusign = await this.sdk.load(
        session.scriptUrl,
        session.integrationKey
      );
      if (this.abandoned()) {
        return;
      }
      const signing = docusign.signing({
        url: session.url,
        displayFormat: 'focused',
      });
      signing.on('ready', () => {
        clearTimeout(this.readyTimer);
        this.loading.set(false);
      });
      signing.on('sessionEnd', (event: unknown) =>
        this.settle(outcomeFromSessionEnd(event))
      );
      const target = this.mount()?.nativeElement;
      if (!target) {
        throw new Error('The signing view has nowhere to mount.');
      }
      signing.mount(target);
    } catch {
      if (this.abandoned()) {
        return;
      }
      // The one-time URL is still unused, so the full-page ceremony can take it.
      this.redirectToCeremony(session.url);
      return;
    }

    this.readyTimer = setTimeout(() => {
      if (this.loading() && !this.settled) {
        this.fail(
          'DocuSign did not open the signing view. Close this window and try again.'
        );
      }
    }, this.readyTimeoutMs());
  }

  /** The retry button (HR admin take-over): start again with the retry action's provider. */
  async retry(): Promise<void> {
    const action = this.retryAction();
    if (!action) {
      return;
    }
    this.error.set(null);
    this.canRetry.set(false);
    this.beginLoading('preparing');
    await this.start(action.sessionProvider);
  }

  /** Title-bar close: a cancel, or the failure the dialog is showing. */
  onClose(): void {
    const error = this.error();
    if (error !== null && !this.settled) {
      this.settled = true;
      this.failed.emit(error);
      return;
    }
    this.settle('cancelled');
  }

  /** Full-page navigation to the DocuSign ceremony (kill switch off, or the SDK failed). */
  protected redirectToCeremony(url: string): void {
    window.location.assign(url);
  }

  /** Closed or removed while a start was awaiting: drop the result. */
  private abandoned(): boolean {
    return this.settled || this.destroyed;
  }

  private beginLoading(stage: DocuSignSigningStage): void {
    this.stage.set(stage);
    this.elapsedSeconds.set(0);
    this.loading.set(true);
  }

  private fail(message: string): void {
    clearTimeout(this.readyTimer);
    this.error.set(message);
    this.loading.set(false);
  }

  private settle(outcome: DocuSignSigningOutcome): void {
    if (this.settled) {
      return;
    }
    this.settled = true;
    clearTimeout(this.readyTimer);
    switch (outcome) {
      case 'completed':
        void this.confirmThenComplete();
        break;
      case 'declined':
        this.declined.emit();
        break;
      default:
        this.cancelled.emit();
    }
  }

  /** Record the signing first when the host asked for it; a failed record still completes (the host refreshes either way). */
  private async confirmThenComplete(): Promise<void> {
    const confirm = this.confirmCompletion();
    if (confirm) {
      this.beginLoading('recording');
      try {
        await confirm();
      } catch {
        // The server's own reconcile path (callback, sweep) records it; the host's refresh shows whatever is recorded.
      }
      this.loading.set(false);
    }
    this.completed.emit();
  }
}
